import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Collapse,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  CreditCard as CreditCardIcon,
  CardGiftcard as PackageIcon,
  CardMembership as MembershipIcon,
  AccountBalanceWallet as WalletIcon,
  LocationOn as LocationIcon,
  Room as RoomIcon,
  Category as CategoryIcon,
  Analytics as AnalyticsIcon,
  AutoMode as TemplateIcon,
  Store as StoreIcon,
  LocalOffer as PromoIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useClerk, useUser } from '@clerk/clerk-react';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  {
    group: 'Main',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ],
  },
  {
    group: 'Schedule Management',
    items: [
      { text: 'Classes', icon: <EventIcon />, path: '/classes' },
      { text: 'Class Templates', icon: <TemplateIcon />, path: '/class-templates' },
      { text: 'Bookings', icon: <EventIcon />, path: '/bookings' },
    ],
  },
  {
    group: 'User Management',
    items: [
      { text: 'Users', icon: <PeopleIcon />, path: '/users' },
      { text: 'User Wallets', icon: <WalletIcon />, path: '/user-wallets' },
      { text: 'Instructors', icon: <SchoolIcon />, path: '/instructors' },
    ],
  },
  {
    group: 'Purchasable Items',
    items: [
      { text: 'Credits', icon: <CreditCardIcon />, path: '/credits' },
      { text: 'Credit Packages', icon: <PackageIcon />, path: '/credit-packages' },
      { text: 'Memberships', icon: <MembershipIcon />, path: '/memberships' },
      { text: 'Store Items', icon: <StoreIcon />, path: '/store-items' },
      { text: 'Promo Codes', icon: <PromoIcon />, path: '/promo-codes' },
    ],
  },
  {
    group: 'Location & Space',
    items: [
      { text: 'Locations', icon: <LocationIcon />, path: '/locations' },
      { text: 'Rooms', icon: <RoomIcon />, path: '/rooms' },
    ],
  },
  {
    group: 'Configuration',
    items: [
      { text: 'Class Categories', icon: <CategoryIcon />, path: '/class-categories' },
    ],
  },
  {
    group: 'Analytics & Admin',
    items: [
      { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
      { text: 'Admins', icon: <AdminIcon />, path: '/admins' },
    ],
  },
];

export default function Layout({ children }: LayoutProps) {
  const [open, setOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    'Schedule Management': true,
    'User Management': true,
    'Purchasable Items': true,
    'Location & Space': true,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user } = useUser();

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleGroupToggle = (group: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleProfileMenuClose();
    await signOut();
  };

  return (
    <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Junto Admin
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              {user?.primaryEmailAddress?.emailAddress}
            </Typography>
            <IconButton
              size="small"
              edge="end"
              aria-label="account of current user"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar 
                alt={user?.fullName || 'User'} 
                src={user?.imageUrl}
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </Box>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: open ? drawerWidth : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            transform: open ? 'translateX(0)' : `translateX(-${drawerWidth}px)`,
            transition: 'transform 0.3s',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <Toolbar>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Junto Pay
          </Typography>
        </Toolbar>
        <Divider />
        <List>
          {menuItems.map((section) => (
            <React.Fragment key={section.group}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleGroupToggle(section.group)}>
                  <ListItemText 
                    primary={section.group} 
                    primaryTypographyProps={{ 
                      variant: 'overline',
                      sx: { fontWeight: 'bold' }
                    }} 
                  />
                  {expandedGroups[section.group] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={expandedGroups[section.group]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.items.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ pl: 2 }}>
                      <ListItemButton
                        selected={location.pathname === item.path}
                        onClick={() => navigate(item.path)}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 3,
          transition: 'padding-left 0.3s',
          minHeight: '100vh',
          overflowX: 'auto',
          overflowY: 'auto',
        }}
      >
        <Toolbar />
        <Box sx={{ minWidth: 'fit-content' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}